/**
 * Base Domain Exception
 * Represents business rule violations in the domain layer
 *
 * Domain exceptions carry error codes that can be mapped to i18n keys,
 * following the Clean Architecture principle of keeping the domain
 * independent of infrastructure concerns like UI localization
 */
export class DomainException extends Error {
  /**
   * @param code Error code for i18n mapping (e.g., 'SPECIMEN_NOT_FOUND')
   * @param details Optional additional context about the error
   */
  constructor(
    public readonly code: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(code);
    this.name = 'DomainException';
    Object.setPrototypeOf(this, DomainException.prototype);
  }
}

/**
 * Exception for validation errors
 * Used when business rules are violated (e.g., invalid input)
 */
export class ValidationException extends DomainException {
  constructor(code: string, details?: Record<string, unknown>) {
    super(code, details);
    this.name = 'ValidationException';
    Object.setPrototypeOf(this, ValidationException.prototype);
  }
}

/**
 * Exception for not found resources
 * Used when a requested resource does not exist
 */
export class NotFoundException extends DomainException {
  constructor(code: string, details?: Record<string, unknown>) {
    super(code, details);
    this.name = 'NotFoundException';
    Object.setPrototypeOf(this, NotFoundException.prototype);
  }
}

/**
 * Exception for business rule violations
 * Used when an operation cannot be completed due to business constraints
 */
export class BusinessRuleException extends DomainException {
  constructor(code: string, details?: Record<string, unknown>) {
    super(code, details);
    this.name = 'BusinessRuleException';
    Object.setPrototypeOf(this, BusinessRuleException.prototype);
  }
}
